<template>
    <div class="relative mt-[-8.6rem] flex grow flex-col gap-4 sm:mt-0">
        <div class="absolute top-0 z-10 container flex min-w-full justify-between py-4 sm:hidden">
            <NuxtLink :to="{ name: 'products' }"
                      aria-label="Back to products"
                      class="btn btn-square btn-sm hover:!text-base-content border-0 bg-white/70 hover:bg-white/90">
                <Icon name="mdi:arrow-left" size="18" />
            </NuxtLink>
            <ClientOnly>
                <label class="btn btn-square swap btn-sm border-0 bg-white/70 text-red-700 hover:bg-white/90">
                    <input v-model="isFavourite" type="checkbox" aria-label="Shortlist as your favourite">
                    <Icon name="mdi:heart" size="18" class="swap-on" />
                    <Icon name="mdi:heart-outline" size="18" class="swap-off" />
                </label>
            </ClientOnly>
        </div>

        <dialog ref="imageModal" class="modal" @submit="slideDuration = originalSlideDuration">
            <form method="dialog"
                  class="modal-box max-w-full px-2 pt-3 pb-4 sm:px-6 sm:pb-6"
                  style="max-width: fit-content">
                <div class="mb-2 flex w-full justify-end">
                    <button class="btn btn-circle btn-ghost btn-xs text-base-content sm:btn-sm">
                        ✕
                    </button>
                </div>
                <NuxtImg v-if="imageToDisplay" :src="imageToDisplay" class="mx-auto rounded" />
            </form>
            <form method="dialog" class="modal-backdrop" @submit="slideDuration = originalSlideDuration">
                <button>close</button>
            </form>
        </dialog>

        <div class="flex flex-col justify-between gap-6 sm:container sm:flex-row lg:gap-4">
            <div class="sm:w-1/2">
                <Splide ref="carousel"
                        :options="{
                            rewind: true,
                            autoplay: true,
                            pauseOnHover: true,
                            slideFocus: true,
                            pagination: false,
                            arrows: false
                        }"
                        class="min-w-40"
                        :aria-label="`Images of ${product!.name}`"
                        @splide:move="splide => currentSlide = splide.index">
                    <!-- full width before Splide mounts, otherwise the slides collapse to 0px and the page shifts -->
                    <SplideSlide v-for="(pImage, index) in product!.images" :key="index" class="w-full">
                        <div class="relative overflow-hidden sm:rounded-xl" :style="placeholder(pImage.meta.thumbhash)">
                            <!-- first slide is the LCP; mobile gets a fixed box so the text below doesn't shift.
                                 width/height go on the img only: in the URL they'd create extra Cloudflare transforms -->
                            <NuxtPicture format="auto" legacy-format="auto" :src="pImage.source"
                                         sizes="sm:100vw md:300px lg:500px"
                                         :alt="index === 0 ? product!.name : `${product!.name} (${index + 1})`"
                                         :loading="index === 0 ? 'eager' : 'lazy'"
                                         :fetchpriority="index === 0 ? 'high' : 'low'"
                                         :preload="index === 0"
                                         class="w-full sm:max-h-[60vh]"
                                         :img-attrs="{
                                             width: pImage.meta.width,
                                             height: pImage.meta.height,
                                             class: 'sm:rounded-xl w-full aspect-square object-cover sm:aspect-auto'
                                         }"
                                         @dblclick="() => {
                                             imageToDisplay = pImage.source;
                                             $refs.imageModal.showModal();
                                             slideDuration = 0
                                         }" />
                            <svg xmlns="http://www.w3.org/2000/svg"
                                 viewBox="0 0 1440 320"
                                 class="fill-base-100 absolute -bottom-1 sm:hidden">
                                <!-- eslint-disable-next-line @stylistic/max-len -->
                                <path d="M0,256L60,261.3C120,267,240,277,360,282.7C480,288,600,288,720,277.3C840,267,960,245,1080,245.3C1200,245,1320,267,1380,277.3L1440,288L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z" />
                            </svg>
                        </div>
                    </SplideSlide>
                </Splide>
                <PublicPagination v-if="product!.images.length > 1"
                                  :slides-count="product!.images.length"
                                  :slide-to="$refs.carousel?.go"
                                  :current-slide="currentSlide" />
            </div>
            <section class="container sm:w-1/2 sm:px-0">
                <h1 class="flex flex-wrap items-center justify-center gap-2 text-center font-bold"
                    :style="{ 'view-transition-name': `${product!.slug}-title` }">
                    <ClientOnly>
                        <NuxtLink v-if="authStore.isAuthenticated"
                                  :to="{ name: 'admin-products-id-edit', params: { id: product!.id } }">
                            <Icon name="mdi:pencil" />
                        </NuxtLink>
                    </ClientOnly>
                    {{ product!.name }}
                    <ClientOnly>
                        <div class="tooltip tooltip-left lg:tooltip-bottom hidden md:block"
                             data-tip="Shortlist as your favourite">
                            <PublicHeartButton v-model="isFavourite" class="ml-2" />
                        </div>
                        <button v-if="shareIsSupported"
                                title="Share"
                                class="btn btn-square btn-ghost btn-sm"
                                @click="shareProduct">
                            <Icon name="mdi:share-variant" size="18" />
                        </button>
                    </ClientOnly>
                </h1>
                <p class="text-base-content/80 mt-8 text-center">
                    {{ product!.shortDescription }}
                </p>
                <div class="relative mt-8 max-h-[40vh] overflow-y-auto md:mt-10">
                    <table class="table-xs 2xs:table-sm sm:table-md xl:table-lg table">
                        <thead class="bg-base-100 text-base-content/80 sticky top-0 text-sm">
                            <tr>
                                <th>Price</th>
                                <th colspan="2">
                                    Size
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(variation, index) in product!.variations"
                                v-once
                                :key="index"
                                tabindex="0"
                                class="hover focus:bg-base-200 transition-colors focus:outline-none">
                                <td class="font-bold">
                                    £{{ variation.cost }}
                                </td>
                                <td :colspan="variation.unitComment ? 1 : 2">
                                    {{ `${variation.unitAmount} ${unitDisplayValue(variation.unit,
                                                                                   variation.unitAmount)}` }}
                                </td>
                                <td v-if="variation.unitComment">
                                    <i>({{ variation.unitComment }})</i>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="mt-8 flex justify-center">
                    <NuxtLink :to="{ name: 'contact' }"
                              class="btn btn-primary size-auto max-w-full px-6 py-2 leading-relaxed hover:!text-white">
                        Enquire about<br>{{ product!.name }}
                    </NuxtLink>
                </div>
            </section>
        </div>
        <div v-if="product!.description || product!.ingredients.length"
             class="from-base-200/70 mt-4 w-full grow space-y-8 bg-gradient-to-t pb-4">
            <section v-if="product!.description" class="container">
                <h2 class="sr-only">
                    Description
                </h2>
                <!-- eslint-disable vue/no-v-html -->
                <div class="prose prose-sm sm:prose-base lg:prose-lg dark:prose-invert max-w-none"
                     v-html="product!.description" />
            </section>
            <section v-if="product!.ingredients.length" class="container">
                <h2 class="text-xl">
                    Ingredients
                </h2>
                <small class="text-base-content/60 block">
                    <i>
                        For allergen advice see ingredients in bold. Product may also contain food colouring.
                    </i>
                </small>
                <p class="mt-4">
                    <span v-for="(ingredient, i) in ingredients"
                          :key="i"
                          :class="{ 'font-bold': ingredient.isAllergen }">
                        {{ ingredient.name }}{{ i !== ingredients.length - 1 ? ', ' : '' }}
                    </span>
                </p>
            </section>
            <section v-if="product!.shelfLife"
                     class="text-base-content/60 container flex items-center gap-2 font-semibold italic">
                <h2 class="text-lg">
                    Shelf Life
                </h2>
                <p>
                    - {{ product!.shelfLife }} day{{ product!.shelfLife > 1 ? 's' : '' }}
                </p>
            </section>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css/core';
import type { ProductAttributes } from '~/types';

const placeholder = useThumbhash();

const api = useApi();

const productStore = useProductStore();
const route = useRoute();
const { data: product, error } = await useAsyncData<ProductAttributes>(
    `product-${route.params.slug}`,
    async () => api(`products/${route.params.slug}`),
    {
        // returning null triggers the fetch
        getCachedData: (key, nuxtApp) => {
            const product: ProductAttributes | undefined = nuxtApp.payload.data[key] ?? nuxtApp.static.data[key];

            if (product) {
                return product;
            }

            // try to get the data if we have already loaded the products
            const products: ProductAttributes[] | undefined = nuxtApp.payload.data['products']
                ?? nuxtApp.static.data['products'];

            return products?.find(product => product.slug === route.params.slug);
        }
    }
);

if (!product.value) {
    // only a 404 from the API means it's gone; anything else (e.g. 429) must not tell crawlers to drop the page
    const notFound = !error.value || error.value.statusCode === 404;

    throw createError({
        statusCode: notFound ? 404 : 503,
        statusMessage: notFound ? 'Product not found' : 'Product temporarily unavailable'
    });
} else {
    productStore.productId = product.value.id;
}

const unitStore = useUnitStore();
const authStore = useAuthStore();
const imageToDisplay = ref<string>();
const originalSlideDuration = product.value.images.length === 1 ? 0 : 4000;
const slideDuration = ref(originalSlideDuration);
const image = computed(() => product.value!.images.at(0)!);
const title = computed(() => product.value!.name + ' | Cina\'s Delicious Cakes');
const ingredients = computed(() => product.value!.ingredients
    .flatMap(ingredient => ingredient.subIngredients?.length
        ? ingredient.subIngredients
        : [ingredient])
    .sort((a, b) => a.name.localeCompare(b.name)));
const currentSlide = ref(0);
const { isSupported: shareIsSupported, share } = import.meta.client
    ? useShare({
            title: title.value,
            text: 'Check out this product I found on Cina\'s Delicious Cakes',
            url: window.location.href
        })
    : {
            isSupported: computed(() => false),
            share: async () => Promise.resolve()
        } as ReturnType<typeof useShare>;
const shareProduct = async () => {
    if (!shareIsSupported.value) {
        return;
    }

    await share()
        .then(() => {
            useTrackEvent('share', {
                /* eslint-disable @typescript-eslint/naming-convention */
                content_type: 'website',
                items: [
                    {
                        item_id: product.value!.id,
                        item_name: product.value!.name,
                        item_category: product.value!.type
                    }
                ]
                /* eslint-enable @typescript-eslint/naming-convention */
            });
            return;
        })
        .catch(e => {
            if (e instanceof Error && e.message.includes('Share cancelled')) {
                return;
            }
        });
};

useSeoMeta({
    title,
    description: product.value.shortDescription,
    ogTitle: title,
    ogDescription: product.value.shortDescription,
    twitterTitle: title,
    twitterDescription: product.value.shortDescription,

    ogImageHeight: image.value.meta.height,
    ogImageWidth: image.value.meta.width,
    ogImageAlt: product.value.name,
    ogImageUrl: image.value.source,
    ogImageType: 'image/jpeg',
    twitterImage: image.value.source,
    twitterImageAlt: product.value.name
});

const unitDisplayValue = unitStore.getDisplayName;

const { isFavourite } = useProductFavourites(productStore.productId!);
const url = useRequestURL();

/* eslint-disable @typescript-eslint/naming-convention */
useSchemaOrg([
    defineWebPage({
        '@type': 'ItemPage'
    }),
    defineProduct({
        name: product.value.name,
        description: product.value.shortDescription,
        image: product.value.images.map(image => image.source),
        brand: {
            '@type': 'Brand',
            name: 'Cina\'s Delicious Cakes'
        },
        offers: defineAggregateOffer({
            lowPrice: product.value.variations.at(0)!.cost!,
            highPrice: product.value.variations.at(product.value.variations.length - 1)!.cost!,
            offerCount: product.value.variations.length,
            priceCurrency: 'GBP',
            offers: product.value.variations.map(variation => defineOffer({
                price: variation.cost!,
                priceCurrency: 'GBP',
                hasMerchantReturnPolicy: {
                    applicableCountry: 'GB',
                    name: 'No Returns',
                    returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
                    merchantReturnDays: 0,
                    returnMethod: 'https://schema.org/ReturnNotPermitted',
                    returnFees: 'https://schema.org/ReturnFeesCustomerResponsible',
                    inStoreReturnsOffered: false
                },
                itemCondition: 'https://schema.org/NewCondition',
                availability: 'https://schema.org/PreOrder',
                url: url.href,
                priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
                advanceBookingRequirement: {
                    '@type': 'QuantitativeValue',
                    minValue: 14, // Preferred 2 weeks minimum notice
                    unitCode: 'DAY',
                    description: 'Preferred 2 weeks notice, shorter notice may be possible upon request'
                },
                shippingDetails: {
                    shippingRate: {
                        value: 5.00, // Starting from £5.00
                        currency: 'GBP'
                    },
                    shippingDestination: {
                        addressCountry: 'GB'
                    },
                    deliveryTime: {
                        transitTime: {
                            minValue: 0, // Same day delivery possible
                            maxValue: 1, // Usually within a day
                            unitCode: 'DAY'
                        },
                        handlingTime: {
                            minValue: 7, // Can potentially accommodate shorter notice
                            maxValue: 14, // Standard preparation time
                            unitCode: 'DAY',
                            description: 'Standard preparation time is 2 weeks, but shorter notice may be possible '
                                + 'upon request'
                        }
                    },
                    availableDeliveryMethod: ['http://schema.org/OnSitePickup', 'http://schema.org/ParcelService']
                },
                paymentMethod: ['Cash', 'CreditCard', 'BankTransfer'],
                eligibleRegion: {
                    '@type': 'Country',
                    name: 'United Kingdom'
                }
            }))
        })
    })
]);
/* eslint-enable @typescript-eslint/naming-convention */

if (import.meta.client) {
    addEventListener('blur', () => slideDuration.value = 0);
    addEventListener('focus', () => slideDuration.value = originalSlideDuration);

    onUnmounted(() => {
        productStore.productId = null;
        addEventListener('blur', () => slideDuration.value = 0);
        addEventListener('focus', () => slideDuration.value = originalSlideDuration);
    });
}

defineOptions({
    // eslint-disable-next-line vue/match-component-file-name,vue/component-definition-name-casing
    name: 'product'
});
</script>

<style>
/* so carousel items start from the top of the page */
.carousel__track {
    align-items: flex-start !important;
}
</style>
